import React, { useState, useMemo } from 'react';
import { Integration, IntegrationStatus, IntegrationTemplate, Portco } from '../types';
import { API_BASE, apiFetch } from '../api/base';
import {
  Plug,
  PlugZap,
  Search,
  Filter,
  Settings,
  CheckCircle2,
  XCircle,
  AlertCircle,
  Clock,
  Plus,
  Activity,
  Database,
  X,
  Save,
  RefreshCw,
  Zap,
  Shield,
  Trash2,
  Link2Off,
} from 'lucide-react';

// Relative time formatter helper
const formatRelativeTime = (isoOrText: string): string => {
  if (!isoOrText || isoOrText === '—') return '—';
  const timestamp = new Date(isoOrText).getTime();
  if (isNaN(timestamp)) return isoOrText;

  const diffMs = Date.now() - timestamp;
  if (diffMs < 0) return 'Just now';

  const diffMins = Math.floor(diffMs / (1000 * 60));
  if (diffMins < 1) return 'Just now';
  if (diffMins < 60) return `${diffMins} min ago`;

  const diffHours = Math.floor(diffMins / 60);
  if (diffHours < 24) return `${diffHours} hr${diffHours > 1 ? 's' : ''} ago`;

  const diffDays = Math.floor(diffHours / 24);
  return `${diffDays} day${diffDays > 1 ? 's' : ''} ago`;
};

// Category badge color helper
const getCategoryColor = (category: string) => {
  switch (category) {
    case 'EHR':
      return 'bg-mantis-50 text-mantis-700 border-mantis-200';
    case 'Marketing':
      return 'bg-indigo-50 text-indigo-700 border-indigo-200';
    case 'HR':
      return 'bg-purple-50 text-purple-700 border-purple-200';
    case 'Finance':
      return 'bg-emerald-50 text-emerald-700 border-emerald-200';
    case 'Communication':
      return 'bg-blue-50 text-blue-700 border-blue-200';
    case 'Intake':
      return 'bg-teal-50 text-teal-700 border-teal-200';
    case 'Analytics':
      return 'bg-amber-50 text-amber-700 border-amber-200';
    default:
      return 'bg-slate-100 text-slate-600 border-slate-200';
  }
};

// Status badge helper
const getStatusBadge = (status: IntegrationStatus) => {
  switch (status) {
    case 'connected':
      return (
        <span className="inline-flex items-center px-2.5 py-1 rounded-full text-xs font-semibold bg-emerald-50 text-emerald-700 border border-emerald-300">
          <span className="w-1.5 h-1.5 rounded-full bg-emerald-500 animate-pulse mr-1.5" />
          Connected
        </span>
      );
    case 'disconnected':
      return (
        <span className="inline-flex items-center px-2.5 py-1 rounded-full text-xs font-semibold bg-slate-100 text-slate-600 border border-slate-300">
          <span className="w-1.5 h-1.5 rounded-full bg-slate-400 mr-1.5" />
          Disconnected
        </span>
      );
    case 'error':
      return (
        <span className="inline-flex items-center px-2.5 py-1 rounded-full text-xs font-semibold bg-rose-50 text-rose-700 border border-rose-300">
          <span className="w-1.5 h-1.5 rounded-full bg-rose-500 animate-ping mr-1.5" />
          Error
        </span>
      );
    case 'pending':
      return (
        <span className="inline-flex items-center px-2.5 py-1 rounded-full text-xs font-semibold bg-amber-50 text-amber-700 border border-amber-300">
          <span className="w-1.5 h-1.5 rounded-full bg-amber-500 animate-pulse mr-1.5" />
          Pending
        </span>
      );
  }
};

// Health score circular progress ring component
const HealthScoreCircle: React.FC<{ score: number }> = ({ score }) => {
  const radius = 16;
  const circumference = 2 * Math.PI * radius;
  const strokeDashoffset = circumference - (score / 100) * circumference;

  const scoreColor =
    score >= 90
      ? 'text-emerald-600'
      : score >= 75
      ? 'text-mantis-600'
      : score >= 50
      ? 'text-amber-500'
      : 'text-rose-500';

  const strokeColor =
    score >= 90
      ? '#34d399'
      : score >= 75
      ? '#22d3ee'
      : score >= 50
      ? '#fbbf24'
      : '#f87171';

  return (
    <div className="flex items-center space-x-2.5">
      <div className="relative w-10 h-10 flex items-center justify-center shrink-0">
        <svg className="w-10 h-10 transform -rotate-90">
          <circle
            cx="20"
            cy="20"
            r={radius}
            className="text-slate-800"
            strokeWidth="3.5"
            stroke="currentColor"
            fill="transparent"
          />
          <circle
            cx="20"
            cy="20"
            r={radius}
            strokeWidth="3.5"
            strokeDasharray={circumference}
            strokeDashoffset={strokeDashoffset}
            strokeLinecap="round"
            stroke={strokeColor}
            fill="transparent"
            className="transition-all duration-500 ease-out"
          />
        </svg>
        <span className={`absolute text-[10px] font-bold ${scoreColor}`}>
          {score}%
        </span>
      </div>
      <div>
        <div className="text-[10px] uppercase font-bold text-slate-400 tracking-wider">
          Health Score
        </div>
        <div className={`text-xs font-semibold ${scoreColor}`}>
          {score >= 90 ? 'Optimal' : score >= 75 ? 'Good' : score >= 50 ? 'Degraded' : 'Offline'}
        </div>
      </div>
    </div>
  );
};

import { usePortCo } from '../context/PortCoContext';
import { PORTCOS } from '../mock/platformData';
import { Building2 } from 'lucide-react';

export const IntegrationHub: React.FC = () => {
  // ── Multi-tenant state: every portco has its OWN connection instances ──
  const { selectedPortco, selectedPortcoName } = usePortCo();
  const isAllView = selectedPortco === 'all';

  // ── Live backend state (Cosmos-backed via GET /api/integrations) ──
  // Platform-wide catalog templates + per-portco instances keyed by business.
  const [templates, setTemplates] = useState<IntegrationTemplate[]>([]);
  // Live PortCo registry. Bootstrap mock data is only a resilience fallback if
  // an older API response has no tenant roster.
  const [tenants, setTenants] = useState<Portco[]>(PORTCOS);
  const [portcoIntegrations, setPortcoIntegrations] = useState<Record<string, Integration[]>>({});
  const [isLoading, setIsLoading] = useState(true);
  const [loadError, setLoadError] = useState<string | null>(null);

  // Fetch the live template catalog + every business's instances and map
  // them into the UI shape. All mutations call the API then refresh here.
  const refresh = React.useCallback(async () => {
    try {
      const res = await apiFetch(`${API_BASE}/integrations`);
      if (!res.ok) throw new Error(`API returned ${res.status}`);
      const data = await res.json();
      setTemplates(
        (data.templates ?? []).map((t: any) => ({
          templateId: t.templateId,
          name: t.name,
          category: t.category,
          vendor: t.vendor,
          description: t.description,
          available: !!t.available,
          configFields: t.configFields ?? [],
        }))
      );
      if (Array.isArray(data.tenants) && data.tenants.length > 0) {
        setTenants(data.tenants.map((t: any) => ({
          id: t.id,
          name: t.name,
          code: t.code ?? t.id,
          region: t.region ?? 'Unassigned',
        })));
      }
      const byPortco: Record<string, Integration[]> = {};
      for (const inst of data.instances ?? []) {
        const ui: Integration = {
          id: inst.id,
          templateId: inst.templateId,
          portcoId: inst.portcoId,
          name: inst.name,
          category: inst.category,
          status: inst.status,
          vendor: inst.vendor,
          lastSync: inst.lastSync || '—',
          description: inst.description,
          healthScore: inst.healthScore ?? 0,
          dataPoints: inst.dataPoints ?? 0,
          configFields: (inst.configFields ?? []).map((f: any) => ({
            ...f,
            value: inst.config?.[f.key] ?? f.value ?? '',
          })),
        };
        (byPortco[inst.portcoId] ||= []).push(ui);
      }
      setPortcoIntegrations(byPortco);
      setLoadError(null);
    } catch {
      setLoadError('Failed to load integrations from the platform API.');
    } finally {
      setIsLoading(false);
    }
  }, []);

  React.useEffect(() => {
    refresh();
  }, [refresh]);

  // Instances visible in the current scope
  const integrations = useMemo(() => {
    if (isAllView) return Object.values(portcoIntegrations).flat();
    return portcoIntegrations[selectedPortco] ?? [];
  }, [portcoIntegrations, selectedPortco, isAllView]);

  const [searchQuery, setSearchQuery] = useState('');
  const [selectedCategory, setSelectedCategory] = useState<string>('All');
  const [selectedStatus, setSelectedStatus] = useState<string>('All');

  // Modal State
  const [configuringIntegration, setConfiguringIntegration] = useState<Integration | null>(null);
  const [configFormData, setConfigFormData] = useState<Record<string, string>>({});
  const [isSavingConfig, setIsSavingConfig] = useState(false);
  const [isTesting, setIsTesting] = useState(false);
  const [testResult, setTestResult] = useState<{
    status: 'success' | 'error' | 'timeout';
    latency: number;
    endpoint: string;
    message: string;
    httpStatus?: number;
  } | null>(null);

  // Marketplace Modal State
  const [showMarketplaceModal, setShowMarketplaceModal] = useState(false);

  // Notification Toast State
  const [toastMessage, setToastMessage] = useState<string | null>(null);

  const showToast = (msg: string) => {
    setToastMessage(msg);
    setTimeout(() => {
      setToastMessage(null);
    }, 3500);
  };

  // Filter options
  const categories = ['All', 'EHR', 'Marketing', 'HR', 'Finance', 'Intake', 'Communication', 'CRM'];
  const statuses = ['All', 'Connected', 'Disconnected', 'Error', 'Pending'];

  const matchesFilters = (item: Integration) => {
    const matchesSearch =
      item.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
      item.vendor.toLowerCase().includes(searchQuery.toLowerCase()) ||
      item.description.toLowerCase().includes(searchQuery.toLowerCase());
    const matchesCategory =
      selectedCategory === 'All' || item.category === selectedCategory;
    const matchesStatus =
      selectedStatus === 'All' ||
      item.status.toLowerCase() === selectedStatus.toLowerCase();
    return matchesSearch && matchesCategory && matchesStatus;
  };

  // Grouped view (All Businesses): one card per adapter, showing each portco's instance
  const groupedTemplates = useMemo(() => {
    return templates.map((tpl) => {
      const instances = integrations
        .filter((i) => i.templateId === tpl.templateId)
        .filter(matchesFilters);
      return { tpl, instances };
    }).filter((g) => selectedCategory === 'All' || g.tpl.category === selectedCategory);
  }, [templates, integrations, searchQuery, selectedCategory, selectedStatus]);

  // Flat view (specific portco): that business's instances only
  const filteredIntegrations = useMemo(() => {
    return integrations.filter(matchesFilters);
  }, [integrations, searchQuery, selectedCategory, selectedStatus]);

  // Statistics are tenant-led: every onboarded PortCo owns one isolated
  // slot for every catalog adapter, whether configured yet or not.
  const stats = useMemo(() => {
    const tenantCount = isAllView ? tenants.length : 1;
    const total = tenantCount * templates.length;
    const connectedList = integrations.filter((i) => i.status === 'connected');
    const connected = connectedList.length;
    const needsSetup = Math.max(0, total - connected);
    const avgHealth =
      connected > 0
        ? Math.round(connectedList.reduce((acc, curr) => acc + curr.healthScore, 0) / connected)
        : 0;
    return { total, connected, needsSetup, avgHealth, tenantCount };
  }, [integrations, templates.length, tenants.length, isAllView]);


  const getPortcoName = (portcoId: string) =>
    tenants.find((p) => p.id === portcoId)?.name ?? portcoId;

  // Toggle Connect / Disconnect — calls the real backend for ONE business's
  // instance. Connect verifies the actual data path server-side (managed
  // identity) and only marks connected when the path verifies.
  const handleToggleConnect = async (item: Integration) => {
    const isConnected = item.status === 'connected';
    try {
      const res = await apiFetch(
        `${API_BASE}/integrations/${encodeURIComponent(item.templateId)}/${isConnected ? 'disconnect' : 'connect'}?portcoId=${encodeURIComponent(item.portcoId)}`,
        { method: 'POST' }
      );
      const body = await res.json().catch(() => ({} as any));
      if (!res.ok) {
        showToast(`✗ ${body.error || 'Operation failed'}`);
        return;
      }
      showToast(
        isConnected
          ? `Disconnected ${item.name} for ${getPortcoName(item.portcoId)}`
          : `Connected ${item.name} for ${getPortcoName(item.portcoId)}!`
      );
    } catch (err: any) {
      showToast(`✗ Network error: ${err.message || 'request failed'}`);
    } finally {
      refresh();
    }
  };

  // Open Configuration Modal (per-portco config values)
  const handleOpenConfig = (item: Integration) => {
    setConfiguringIntegration(item);
    const initialData: Record<string, string> = {};
    item.configFields.forEach((field) => {
      initialData[field.key] = field.value || '';
    });
    setConfigFormData(initialData);
    setTestResult(null);
  };

  // Remove this business's instance entirely (per-tenant DELETE).
  const handleRemoveConnection = async (item: Integration) => {
    if (!window.confirm(`Remove ${item.name} for ${getPortcoName(item.portcoId)}? Credentials and configuration for this business will be deleted.`)) return;
    try {
      const res = await apiFetch(
        `${API_BASE}/integrations/${encodeURIComponent(item.templateId)}?portcoId=${encodeURIComponent(item.portcoId)}`,
        { method: 'DELETE' }
      );
      const body = await res.json().catch(() => ({} as any));
      if (!res.ok) {
        showToast(`✗ ${body.error || 'Delete failed'}`);
        return;
      }
      showToast(`Removed ${item.name} from ${getPortcoName(item.portcoId)}`);
    } catch (err: any) {
      showToast(`✗ Network error: ${err.message || 'request failed'}`);
    } finally {
      refresh();
    }
  };

  // Build a draft (unsaved) instance from a catalog template and open the
  // config modal. Saving creates the real per-portco instance via PUT.
  const handleAddConnection = (tpl: IntegrationTemplate, portcoId: string) => {
    const draft: Integration = {
      id: `draft:${tpl.templateId}:${portcoId}`,
      templateId: tpl.templateId,
      portcoId,
      name: tpl.name,
      category: tpl.category,
      status: 'disconnected',
      vendor: tpl.vendor,
      description: tpl.description,
      healthScore: 0,
      dataPoints: 0,
      lastSync: '—',
      configFields: tpl.configFields.map((f) => ({ ...f, value: '' })),
    };
    handleOpenConfig(draft);
  };

  // Save Configuration — PUTs to the real backend for this portco's instance.
  // Creates the instance on first save; password fields go through the
  // encrypted-secrets channel (wrapped under the per-portco KEK).
  const handleSaveConfig = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!configuringIntegration) return;

    setIsSavingConfig(true);
    const target = configuringIntegration;
    try {
      const config: Record<string, string> = {};
      const secrets: Record<string, string> = {};
      target.configFields.forEach((field) => {
        const value = configFormData[field.key];
        if (!value) return;
        if (field.type === 'password') secrets[field.key] = value;
        else config[field.key] = value;
      });

      const res = await apiFetch(
        `${API_BASE}/integrations/${encodeURIComponent(target.templateId)}?portcoId=${encodeURIComponent(target.portcoId)}`,
        {
          method: 'PUT',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ config, secrets }),
        }
      );
      const body = await res.json().catch(() => ({} as any));
      if (!res.ok) {
        showToast(`✗ ${body.error || 'Save failed'}`);
        return;
      }
      showToast(`Configuration saved — ${target.name} · ${getPortcoName(target.portcoId)}`);
      setConfiguringIntegration(null);
      refresh();
    } catch (err: any) {
      showToast(`✗ Network error: ${err.message || 'request failed'}`);
    } finally {
      setIsSavingConfig(false);
    }
  };

  // Test Connection — runs the REAL server-side connectivity check via the
  // platform backend (managed identity), not a browser fetch. For SETWorks
  // CSV mode this verifies the actual blob container data path.
  const handleTestConnection = async () => {
    if (!configuringIntegration) return;
    const target = configuringIntegration;
    setIsTesting(true);
    setTestResult(null);

    try {
      // Persist the currently-entered config first so the test runs against
      // exactly what the user sees in the form.
      const config: Record<string, string> = {};
      const secrets: Record<string, string> = {};
      target.configFields.forEach((field) => {
        const value = configFormData[field.key];
        if (!value) return;
        if (field.type === 'password') secrets[field.key] = value;
        else config[field.key] = value;
      });
      await apiFetch(
        `${API_BASE}/integrations/${encodeURIComponent(target.templateId)}?portcoId=${encodeURIComponent(target.portcoId)}`,
        {
          method: 'PUT',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ config, secrets }),
        }
      );

      const res = await apiFetch(
        `${API_BASE}/integrations/${encodeURIComponent(target.templateId)}/test?portcoId=${encodeURIComponent(target.portcoId)}`,
        { method: 'POST' }
      );
      const body = await res.json().catch(() => ({} as any));
      const t = body.testResult;
      if (!res.ok || !t) {
        setTestResult({
          status: 'error',
          latency: 0,
          endpoint: 'platform backend',
          message: body.error || 'Test failed.',
        });
        showToast(`✗ ${target.name}: ${body.error || 'test failed'}`);
        return;
      }

      setTestResult({
        status: t.status === 'success' ? 'success' : 'error',
        latency: t.latencyMs ?? 0,
        endpoint: 'platform backend (managed identity)',
        message: t.message,
      });
      showToast(
        t.status === 'success'
          ? `✓ ${target.name} verified — ${t.latencyMs}ms`
          : `✗ ${target.name}: ${t.message}`
      );
      refresh();
    } catch (err: any) {
      setTestResult({
        status: 'error',
        latency: 0,
        endpoint: 'platform backend',
        message: `Network error: ${err.message || 'request failed'}.`,
      });
      showToast(`✗ Network error: ${err.message || 'request failed'}`);
    } finally {
      setIsTesting(false);
    }
  };

  return (
    <div className="space-y-6 pb-12">
      {/* Live-data status banner */}
      {isLoading && (
        <div className="flex items-center gap-2 text-xs font-medium text-slate-500 bg-slate-50 border border-slate-100 rounded-xl px-4 py-2.5">
          <RefreshCw className="w-3.5 h-3.5 animate-spin text-mantis-600" />
          Loading integrations from the platform API...
        </div>
      )}
      {!isLoading && loadError && (
        <div className="flex items-center gap-2 text-xs font-medium text-rose-600 bg-rose-50 border border-rose-200 rounded-xl px-4 py-2.5">
          <AlertCircle className="w-3.5 h-3.5" />
          {loadError}
          <button onClick={() => refresh()} className="ml-auto underline hover:no-underline">
            Retry
          </button>
        </div>
      )}

      {/* Toast Notification */}
      {toastMessage && (
        <div className="fixed bottom-6 right-6 z-50 flex items-center space-x-2 bg-mantis-50 border border-cyan-700 text-mantis-600 px-4 py-3 rounded-xl shadow-2xl animate-fade-in">
          <Zap className="w-4 h-4 text-mantis-600 animate-bounce" />
          <span className="text-xs font-semibold">{toastMessage}</span>
        </div>
      )}

      {/* 1. Header & Subtitle */}
      <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4">
        <div>
          <div className="flex items-center space-x-3">
            <div className="p-2.5 rounded-xl bg-gradient-to-tr from-mantis-500/20 to-redwood-500/20 border border-cyan-800/50 text-mantis-600">
              <Plug className="w-6 h-6" />
            </div>
            <div>
              <h1 className="text-2xl font-bold text-slate-800 tracking-tight flex items-center gap-2">
                Integration Hub
                <span className="text-[10px] font-bold uppercase tracking-wider bg-mantis-50 text-mantis-600 px-2 py-0.5 rounded border border-mantis-200">
                  Live Adapters
                </span>
              </h1>
              <p className="text-xs text-slate-400 mt-0.5">
                {isAllView
                  ? `Connections are managed per business. Viewing all ${tenants.length} portfolio companies — select a business to configure its connections.`
                  : `Managing ${selectedPortcoName}'s own tenant connections, credentials, and sync health.`}
              </p>
            </div>
          </div>
        </div>

        {/* Quick Action */}
        <button
          onClick={() => setShowMarketplaceModal(true)}
          className="inline-flex items-center space-x-2 bg-gradient-to-r from-mantis-500 to-redwood-500 text-white px-4 py-2.5 rounded-xl text-xs font-semibold shadow-lg shadow-mantis-200/50 transition-all self-start lg:self-auto"
        >
          <Plus className="w-4 h-4" />
          <span>Add Integration{!isAllView ? ` to ${selectedPortcoName}` : ''}</span>
        </button>
      </div>

      {/* Search and Filters Bar */}
      <div className="bg-slate-50 border border-slate-100 rounded-2xl p-4 space-y-4 shadow-xl backdrop-blur-md">
        <div className="flex flex-col md:flex-row items-stretch md:items-center gap-3">
          {/* Search Box */}
          <div className="relative flex-1">
            <Search className="w-4 h-4 absolute left-3.5 top-1/2 -translate-y-1/2 text-slate-400" />
            <input
              type="text"
              placeholder="Search integrations by name, vendor, or keywords..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="w-full bg-slate-50 border border-slate-100 rounded-xl pl-10 pr-4 py-2 text-xs text-slate-700 placeholder-slate-400 focus:outline-none focus:border-cyan-500 focus:ring-1 focus:ring-mantis-500 transition-all"
            />
            {searchQuery && (
              <button
                onClick={() => setSearchQuery('')}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-500"
              >
                <X className="w-3.5 h-3.5" />
              </button>
            )}
          </div>

          {/* Status Select Filter */}
          <div className="flex items-center space-x-2 shrink-0">
            <div className="flex items-center space-x-1.5 text-xs text-slate-400 font-medium px-1">
              <Filter className="w-3.5 h-3.5 text-mantis-600" />
              <span>Status:</span>
            </div>
            <select
              value={selectedStatus}
              onChange={(e) => setSelectedStatus(e.target.value)}
              className="bg-slate-50 border border-slate-100 text-slate-700 text-xs rounded-xl px-3 py-2 focus:outline-none focus:border-cyan-500 transition-all cursor-pointer"
            >
              {statuses.map((st) => (
                <option key={st} value={st}>
                  {st}
                </option>
              ))}
            </select>
          </div>
        </div>

        {/* Category Pills */}
        <div className="flex items-center space-x-1.5 overflow-x-auto pt-1 pb-1 scrollbar-none border-t border-slate-100/80">
          <span className="text-[11px] font-semibold text-slate-400 mr-2 uppercase tracking-wider shrink-0">
            Categories:
          </span>
          {categories.map((cat) => {
            const isActive = selectedCategory === cat;
            return (
              <button
                key={cat}
                onClick={() => setSelectedCategory(cat)}
                className={`px-3 py-1.5 rounded-lg text-xs font-semibold whitespace-nowrap transition-all ${
                  isActive
                    ? 'bg-mantis-50 text-mantis-500 border border-cyan-700/80 shadow-sm shadow-cyan-950'
                    : 'bg-slate-50/60 text-slate-400 hover:text-slate-700 hover:bg-white border border-slate-100'
                }`}
              >
                {cat}
              </button>
            );
          })}
        </div>
      </div>

      {/* 2. Stats Row — scoped to current selection */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <div className="bg-slate-50 border border-slate-100 rounded-2xl p-4 shadow-lg flex items-center space-x-3.5">
          <div className="p-3 rounded-xl bg-slate-100 border border-slate-200 text-slate-500">
            <PlugZap className="w-5 h-5" />
          </div>
          <div>
            <div className="text-[10px] font-bold uppercase tracking-wider text-slate-400">
              Tenant Integration Slots
            </div>
            <div className="text-xl font-black text-slate-800 mt-0.5">{stats.total}</div>
          </div>
        </div>

        <div className="bg-slate-50 border border-slate-100 rounded-2xl p-4 shadow-lg flex items-center space-x-3.5">
          <div className="p-3 rounded-xl bg-emerald-950/60 border border-emerald-800/60 text-emerald-500">
            <CheckCircle2 className="w-5 h-5" />
          </div>
          <div>
            <div className="text-[10px] font-bold uppercase tracking-wider text-slate-400">
              Connected
            </div>
            <div className="text-xl font-black text-emerald-500 mt-0.5">{stats.connected}</div>
          </div>
        </div>

        <div className="bg-slate-50 border border-slate-100 rounded-2xl p-4 shadow-lg flex items-center space-x-3.5">
          <div className="p-3 rounded-xl bg-slate-100/90 border border-slate-200 text-slate-400">
            <XCircle className="w-5 h-5" />
          </div>
          <div>
            <div className="text-[10px] font-bold uppercase tracking-wider text-slate-400">
              Needs Setup
            </div>
            <div className="text-xl font-black text-slate-400 mt-0.5">{stats.needsSetup}</div>
          </div>
        </div>

        <div className="bg-slate-50 border border-slate-100 rounded-2xl p-4 shadow-lg flex items-center space-x-3.5">
          <div className="p-3 rounded-xl bg-mantis-50 border border-mantis-200 text-mantis-600">
            <Activity className="w-5 h-5" />
          </div>
          <div>
            <div className="text-[10px] font-bold uppercase tracking-wider text-slate-400">
              Avg Health Score
            </div>
            <div className="text-xl font-black text-mantis-500 mt-0.5">{stats.avgHealth}%</div>
          </div>
        </div>
      </div>

      {/* 3. Integration Cards */}
      {isAllView ? (
        /* ── ALL BUSINESSES: grouped by adapter, one card per vendor with
             a row per portco showing that business's own connection ── */
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {groupedTemplates.map(({ tpl, instances }) => (
            <div
              key={tpl.templateId}
              className="bg-white border border-slate-100 hover:border-slate-200 rounded-2xl p-5 shadow-xl transition-all duration-200 flex flex-col space-y-4"
            >
              <div className="flex items-start justify-between gap-3">
                <div className="flex items-center space-x-3">
                  <div className="w-11 h-11 rounded-xl bg-slate-100 border border-slate-200 flex items-center justify-center text-slate-500 font-bold text-sm shadow-inner shrink-0">
                    {tpl.vendor.substring(0, 2).toUpperCase()}
                  </div>
                  <div>
                    <h3 className="text-base font-bold text-slate-800 leading-tight">{tpl.name}</h3>
                    <p className="text-xs text-slate-400 font-medium">{tpl.vendor}</p>
                  </div>
                </div>
                <span
                  className={`px-2.5 py-0.5 rounded-md text-[10px] font-bold uppercase tracking-wider border ${getCategoryColor(tpl.category)}`}
                >
                  {tpl.category}
                </span>
              </div>

              <p className="text-xs text-slate-500 line-clamp-2 leading-relaxed">{tpl.description}</p>

              {/* One row per portfolio business's own connection */}
              <div className="space-y-2 pt-3 border-t border-slate-100/80">
                {tenants.map((p) => {
                  const inst = instances.find((i) => i.portcoId === p.id);
                  const row = inst ?? {
                    id: `${tpl.templateId}_${p.id}`,
                    templateId: tpl.templateId,
                    portcoId: p.id,
                    name: tpl.name,
                    category: tpl.category,
                    vendor: tpl.vendor,
                    description: tpl.description,
                    status: 'disconnected' as IntegrationStatus,
                    healthScore: 0,
                    dataPoints: 0,
                    lastSync: '—',
                    configFields: [],
                  };
                  return (
                    <div
                      key={p.id}
                      className="flex items-center justify-between gap-3 bg-slate-50/60 border border-slate-100/70 rounded-xl px-3 py-2"
                    >
                      <div className="flex items-center gap-2 min-w-0">
                        <Building2 className="w-3.5 h-3.5 text-slate-400 shrink-0" />
                        <span className="text-xs font-semibold text-slate-600 truncate">
                          {p.name}
                        </span>
                        <span className="text-[10px] text-slate-400 shrink-0">
                          {inst ? `· ${inst.dataPoints.toLocaleString()} pts · ${formatRelativeTime(inst.lastSync)}` : '· not connected'}
                        </span>
                      </div>
                      <div className="flex items-center gap-2 shrink-0">
                        {inst ? getStatusBadge(row.status) : <span className="text-[10px] font-semibold text-slate-500">Needs Setup</span>}
                        {inst ? (
                          <button
                            onClick={() => handleOpenConfig(inst)}
                            className="text-[10px] font-semibold text-mantis-600 hover:text-mantis-500"
                          >
                            Manage
                          </button>
                        ) : (
                          <button
                            onClick={() => handleAddConnection(tpl, p.id)}
                            className="text-[10px] font-semibold text-mantis-600 hover:text-mantis-500"
                          >
                            + Add
                          </button>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          ))}

          {/* Add New Integration Card */}
          <div
            onClick={() => setShowMarketplaceModal(true)}
            className="border-2 border-dashed border-slate-100 hover:border-cyan-500/50 bg-white/30 hover:bg-slate-50 transition-all duration-200 rounded-2xl p-6 flex flex-col items-center justify-center min-h-[250px] cursor-pointer group text-center"
          >
            <div className="w-12 h-12 rounded-full bg-slate-100 group-hover:bg-mantis-50 text-slate-400 group-hover:text-mantis-600 flex items-center justify-center mb-3 border border-slate-200 group-hover:border-mantis-300 transition-all shadow-md">
              <Plus className="w-6 h-6" />
            </div>
            <h3 className="text-sm font-bold text-slate-700 group-hover:text-mantis-500 transition-colors">
              Browse Integration Marketplace
            </h3>
            <p className="text-xs text-slate-400 max-w-xs mt-1 leading-relaxed">
              Provision a new adapter connection for a specific portfolio business.
            </p>
            <span className="inline-flex items-center space-x-1 text-[11px] font-semibold text-mantis-600 mt-3 group-hover:underline">
              <span>Explore {templates.length} Adapters in the Catalog</span>
              <span>&rarr;</span>
            </span>
          </div>
        </div>
      ) : (
        /* ── SPECIFIC BUSINESS: that portco's own connection instances ── */
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {!isLoading && !loadError && templates.length === 0 && (
            <div className="col-span-full border-2 border-dashed border-slate-100 bg-white/50 rounded-2xl p-10 flex flex-col items-center justify-center text-center min-h-[250px]">
              <div className="w-12 h-12 rounded-full bg-slate-100 text-slate-400 flex items-center justify-center mb-4 border border-slate-200">
                <Link2Off className="w-6 h-6" />
              </div>
              <h3 className="text-sm font-bold text-slate-700">
                No integrations yet for {selectedPortcoName}
              </h3>
              <p className="text-xs text-slate-400 max-w-md mt-1.5 leading-relaxed">
                This business has its own isolated integration slots. Add a
                connection below — it will be provisioned, credentialed, and
                encrypted separately from every other portfolio business.
              </p>
              <button
                onClick={() => setShowMarketplaceModal(true)}
                className="mt-5 inline-flex items-center space-x-1.5 bg-gradient-to-r from-mantis-500 to-redwood-500 text-white px-4 py-2 rounded-xl text-xs font-semibold shadow-md hover:shadow-lg transition-all"
              >
                <Plus className="w-4 h-4" />
                <span>Browse Catalog for {selectedPortcoName}</span>
              </button>
            </div>
          )}
          {!isLoading && !loadError && templates
            .filter((tpl) => !integrations.some((i) => i.templateId === tpl.templateId))
            .filter((tpl) => selectedCategory === 'All' || selectedCategory === tpl.category)
            .filter((tpl) => selectedStatus === 'All' || selectedStatus === 'Disconnected')
            .filter((tpl) => !searchQuery || `${tpl.name} ${tpl.vendor} ${tpl.description}`.toLowerCase().includes(searchQuery.toLowerCase()))
            .map((tpl) => (
              <div key={tpl.templateId} className="bg-white border border-slate-100 rounded-2xl p-5 shadow-xl space-y-4">
                <div className="flex justify-between items-start gap-3">
                  <div>
                    <h3 className="text-base font-bold text-slate-800">{tpl.name}</h3>
                    <p className="text-xs text-slate-400">{tpl.vendor} · {selectedPortcoName}</p>
                  </div>
                  <span className="text-xs font-semibold text-slate-500">Needs Setup</span>
                </div>
                <p className="text-xs text-slate-500">{tpl.description}</p>
                <button onClick={() => handleAddConnection(tpl, selectedPortco)}
                  className="text-xs font-semibold text-mantis-600 hover:text-mantis-500">
                  {tpl.available ? 'Configure connection' : 'Request adapter provisioning'}
                </button>
              </div>
            ))}
          {filteredIntegrations.map((item) => {
            const isConnected = item.status === 'connected';

            return (
              <div
                key={item.id}
                className="bg-white border border-slate-100 hover:border-slate-200 rounded-2xl p-5 shadow-xl transition-all duration-200 flex flex-col justify-between space-y-4 relative group"
              >
                <div className="space-y-3">
                  <div className="flex items-start justify-between gap-3">
                    <div className="flex items-center space-x-3">
                      <div className="w-11 h-11 rounded-xl bg-slate-100 border border-slate-200 flex items-center justify-center text-slate-500 font-bold text-sm shadow-inner shrink-0 group-hover:border-cyan-500/40 transition-colors">
                        {item.vendor.substring(0, 2).toUpperCase()}
                      </div>
                      <div>
                        <h3 className="text-base font-bold text-slate-800 leading-tight">
                          {item.name}
                        </h3>
                        <p className="text-xs text-slate-400 font-medium">{item.vendor}</p>
                      </div>
                    </div>

                    <div className="flex flex-col items-end space-y-1.5 shrink-0">
                      <span
                        className={`px-2.5 py-0.5 rounded-md text-[10px] font-bold uppercase tracking-wider border ${getCategoryColor(item.category)}`}
                      >
                        {item.category}
                      </span>
                      {getStatusBadge(item.status)}
                    </div>
                  </div>

                  <p className="text-xs text-slate-500 line-clamp-2 leading-relaxed">
                    {item.description}
                  </p>
                </div>

                {/* Metrics Row */}
                <div className="pt-3 border-t border-slate-100/80 grid grid-cols-3 gap-2 items-center bg-slate-50/40 p-3 rounded-xl border border-slate-100/60">
                  {isConnected ? (
                    <HealthScoreCircle score={item.healthScore} />
                  ) : (
                    <div className="flex items-center space-x-2">
                      <div className="w-10 h-10 rounded-full bg-white border border-slate-200 flex items-center justify-center text-slate-400">
                        <AlertCircle className="w-4 h-4" />
                      </div>
                      <div>
                        <div className="text-[10px] uppercase font-bold text-slate-400 tracking-wider">
                          Health Score
                        </div>
                        <div className="text-xs font-medium text-slate-400">Inactive</div>
                      </div>
                    </div>
                  )}

                  <div className="flex items-center space-x-2">
                    <Clock className="w-4 h-4 text-slate-400 shrink-0" />
                    <div>
                      <div className="text-[10px] uppercase font-bold text-slate-400 tracking-wider">
                        Last Sync
                      </div>
                      <div className="text-xs font-semibold text-slate-700">
                        {formatRelativeTime(item.lastSync)}
                      </div>
                    </div>
                  </div>

                  <div className="flex items-center space-x-2">
                    <Database className="w-4 h-4 text-slate-400 shrink-0" />
                    <div>
                      <div className="text-[10px] uppercase font-bold text-slate-400 tracking-wider">
                        Synced Data
                      </div>
                      <div className="text-xs font-semibold text-slate-700">
                        {item.dataPoints > 0 ? `${item.dataPoints.toLocaleString()} pts` : '0 pts'}
                      </div>
                    </div>
                  </div>
                </div>

                {/* Action Buttons */}
                <div className="flex items-center space-x-3 pt-1">
                  {isConnected ? (
                    <button
                      onClick={() => handleToggleConnect(item)}
                      className="flex-1 flex items-center justify-center space-x-1.5 bg-slate-100 hover:bg-slate-100 text-slate-500 font-semibold text-xs py-2 px-3 rounded-xl border border-slate-200 transition-all"
                    >
                      <XCircle className="w-3.5 h-3.5 text-slate-400" />
                      <span>Disconnect</span>
                    </button>
                  ) : (
                    <button
                      onClick={() => handleToggleConnect(item)}
                      className="flex-1 flex items-center justify-center space-x-1.5 bg-mantis-600 hover:bg-mantis-500 text-white font-semibold text-xs py-2 px-3 rounded-xl shadow-md shadow-mantis-200/40 transition-all"
                    >
                      <Zap className="w-3.5 h-3.5" />
                      <span>Connect</span>
                    </button>
                  )}

                  <button
                    onClick={() => handleOpenConfig(item)}
                    className="flex items-center justify-center space-x-1.5 bg-white hover:bg-slate-100 text-slate-700 font-semibold text-xs py-2 px-3 rounded-xl border border-slate-200 hover:border-slate-300 transition-all"
                    title="Configure Integration Parameters"
                  >
                    <Settings className="w-3.5 h-3.5 text-mantis-600" />
                    <span>Configure</span>
                  </button>

                  <button
                    onClick={() => handleRemoveConnection(item)}
                    className="flex items-center justify-center bg-white hover:bg-rose-50 text-slate-400 hover:text-rose-600 font-semibold text-xs py-2 px-2.5 rounded-xl border border-slate-200 hover:border-rose-200 transition-all"
                    title={`Remove this connection from ${getPortcoName(item.portcoId)}`}
                  >
                    <Trash2 className="w-3.5 h-3.5" />
                  </button>
                </div>
              </div>
            );
          })}

          {/* Add New Integration Card */}
          <div
            onClick={() => setShowMarketplaceModal(true)}
            className="border-2 border-dashed border-slate-100 hover:border-cyan-500/50 bg-white/30 hover:bg-slate-50 transition-all duration-200 rounded-2xl p-6 flex flex-col items-center justify-center min-h-[250px] cursor-pointer group text-center"
          >
            <div className="w-12 h-12 rounded-full bg-slate-100 group-hover:bg-mantis-50 text-slate-400 group-hover:text-mantis-600 flex items-center justify-center mb-3 border border-slate-200 group-hover:border-mantis-300 transition-all shadow-md">
              <Plus className="w-6 h-6" />
            </div>
            <h3 className="text-sm font-bold text-slate-700 group-hover:text-mantis-500 transition-colors">
              Browse Integration Marketplace
            </h3>
            <p className="text-xs text-slate-400 max-w-xs mt-1 leading-relaxed">
              Add another adapter connection for {selectedPortcoName}.
            </p>
            <span className="inline-flex items-center space-x-1 text-[11px] font-semibold text-mantis-600 mt-3 group-hover:underline">
              <span>Explore {templates.length} Adapters in the Catalog</span>
              <span>&rarr;</span>
            </span>
          </div>
        </div>
      )}

      {/* 4. Configuration Modal — portco-scoped */}
      {configuringIntegration && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-slate-50/80 backdrop-blur-sm animate-fade-in">
          <div className="bg-white border border-slate-100 rounded-2xl shadow-2xl max-w-lg w-full overflow-hidden flex flex-col max-h-[90vh]">
            {/* Modal Header */}
            <div className="px-6 py-4 border-b border-slate-100 flex items-center justify-between bg-white">
              <div className="flex items-center space-x-3">
                <div className="p-2 rounded-xl bg-mantis-50 border border-mantis-200 text-mantis-600">
                  <Settings className="w-5 h-5" />
                </div>
                <div>
                  <h2 className="text-base font-bold text-slate-800">
                    Configure {configuringIntegration.name}
                  </h2>
                  <p className="text-xs text-slate-400 flex items-center gap-1">
                    <Building2 className="w-3 h-3" />
                    {getPortcoName(configuringIntegration.portcoId)} • {configuringIntegration.vendor} • {configuringIntegration.category} Adapter
                  </p>
                </div>
              </div>
              <button
                onClick={() => setConfiguringIntegration(null)}
                className="p-1.5 rounded-lg text-slate-400 hover:text-slate-800 hover:bg-slate-100 transition-colors"
              >
                <X className="w-4 h-4" />
              </button>
            </div>

            {/* Modal Form Content */}
            <form onSubmit={handleSaveConfig} className="p-6 space-y-4 overflow-y-auto">
              <div className="bg-mantis-50/30 border border-mantis-200 rounded-xl p-3 flex items-start space-x-2.5 text-xs text-mantis-500">
                <Shield className="w-4 h-4 text-mantis-600 shrink-0 mt-0.5" />
                <div>
                  <span className="font-semibold block">HIPAA & SOC-2 Compliant Storage</span>
                  Credentials for this business are encrypted with its per-portco CMK using AES-256 before storing.
                </div>
              </div>

              {configuringIntegration.configFields.map((field) => (
                <div key={field.key} className="space-y-1.5">
                  <label className="block text-xs font-semibold text-slate-500">
                    {field.label}
                    {field.required && <span className="text-rose-500 ml-1">*</span>}
                  </label>

                  {field.type === 'select' ? (
                    <select
                      value={configFormData[field.key] || ''}
                      onChange={(e) =>
                        setConfigFormData({ ...configFormData, [field.key]: e.target.value })
                      }
                      required={field.required}
                      className="w-full bg-slate-50 border border-slate-100 rounded-xl px-3.5 py-2 text-xs text-slate-800 focus:outline-none focus:border-cyan-500 focus:ring-1 focus:ring-mantis-500 transition-all cursor-pointer"
                    >
                      <option value="" disabled>
                        Select option...
                      </option>
                      {field.options?.map((opt) => (
                        <option key={opt} value={opt}>
                          {opt}
                        </option>
                      ))}
                    </select>
                  ) : (
                    <input
                      type={field.type}
                      value={configFormData[field.key] || ''}
                      onChange={(e) =>
                        setConfigFormData({ ...configFormData, [field.key]: e.target.value })
                      }
                      required={field.required}
                      placeholder={`Enter ${field.label.toLowerCase()}...`}
                      className="w-full bg-slate-50 border border-slate-100 rounded-xl px-3.5 py-2 text-xs text-slate-800 placeholder-slate-600 focus:outline-none focus:border-cyan-500 focus:ring-1 focus:ring-mantis-500 transition-all"
                    />
                  )}
                </div>
              ))}

              {/* Modal Footer Controls */}
              <div className="pt-4 border-t border-slate-100 flex items-center justify-between gap-3">
                <div className="flex flex-col gap-2 w-full">
                  <div className="flex items-center justify-between gap-3">
                    <button
                      type="button"
                      onClick={handleTestConnection}
                      disabled={isTesting}
                      className="inline-flex items-center space-x-1.5 px-3 py-2 rounded-xl text-xs font-semibold bg-slate-100 hover:bg-slate-100 text-slate-500 border border-slate-200 transition-all"
                    >
                      <RefreshCw className={`w-3.5 h-3.5 ${isTesting ? 'animate-spin text-mantis-600' : ''}`} />
                      <span>{isTesting ? 'Testing...' : 'Test Endpoint'}</span>
                    </button>
                    <span className="text-[10px] text-slate-400 font-medium">
                      {testResult ? `Last test: ${testResult.latency}ms` : ''}
                    </span>
                  </div>
                  {testResult && (
                    <div className={`mt-1 p-3 rounded-xl border text-xs space-y-1.5 ${
                      testResult.status === 'success'
                        ? 'bg-emerald-50 border-emerald-200 text-emerald-700'
                        : testResult.status === 'timeout'
                        ? 'bg-amber-50 border-amber-200 text-amber-700'
                        : 'bg-rose-50 border-rose-200 text-rose-700'
                    }`}>
                      <div className="flex items-center gap-2 font-semibold">
                        {testResult.status === 'success' ? (
                          <><CheckCircle2 className="w-4 h-4" /> Endpoint Reachable</>
                        ) : testResult.status === 'timeout' ? (
                          <><AlertCircle className="w-4 h-4" /> Request Timed Out</>
                        ) : (
                          <><XCircle className="w-4 h-4" /> Connection Failed</>
                        )}
                        {testResult.httpStatus && (
                          <span className="ml-auto px-2 py-0.5 rounded-full bg-white/60 text-[10px] font-bold">
                            HTTP {testResult.httpStatus}
                          </span>
                        )}
                      </div>
                      <div className="flex items-center gap-1.5 text-[11px] opacity-80">
                        <Activity className="w-3 h-3" />
                        <span>Latency: {testResult.latency}ms</span>
                        <span className="mx-1">•</span>
                        <span className="font-mono text-[10px] truncate">{testResult.endpoint}</span>
                      </div>
                      <p className="text-[11px] leading-relaxed opacity-90">{testResult.message}</p>
                    </div>
                  )}
                </div>

                <div className="flex items-center space-x-2">
                  <button
                    type="button"
                    onClick={() => setConfiguringIntegration(null)}
                    className="px-4 py-2 rounded-xl text-xs font-semibold bg-slate-100 hover:bg-slate-100 text-slate-500 border border-slate-200 transition-all"
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    disabled={isSavingConfig}
                    className="px-4 py-2 rounded-xl text-xs font-semibold bg-gradient-to-r from-mantis-500 to-redwood-500 text-white shadow-md hover:shadow-lg transition-all disabled:opacity-60"
                  >
                    {isSavingConfig ? 'Saving...' : 'Save Configuration'}
                  </button>
                </div>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* 5. Marketplace Modal */}
      {showMarketplaceModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-slate-50/80 backdrop-blur-sm animate-fade-in">
          <div className="bg-white border border-slate-100 rounded-2xl shadow-2xl max-w-2xl w-full overflow-hidden flex flex-col max-h-[90vh]">
            <div className="px-6 py-4 border-b border-slate-100 flex items-center justify-between">
              <div className="flex items-center space-x-3">
                <div className="p-2 rounded-xl bg-mantis-50 border border-mantis-200 text-mantis-600">
                  <Plus className="w-5 h-5" />
                </div>
                <div>
                  <h2 className="text-base font-bold text-slate-800">Integration Marketplace</h2>
                  <p className="text-xs text-slate-400 flex items-center gap-1">
                    {isAllView ? (
                      <>Select a business first — connections are provisioned per portfolio company</>
                    ) : (
                      <><Building2 className="w-3 h-3" /> Provisioning for {selectedPortcoName}</>
                    )}
                  </p>
                </div>
              </div>
              <button
                onClick={() => setShowMarketplaceModal(false)}
                className="p-1.5 rounded-lg text-slate-400 hover:text-slate-800 hover:bg-slate-100 transition-colors"
              >
                <X className="w-4 h-4" />
              </button>
            </div>

            <div className="p-6 space-y-4 max-h-[70vh] overflow-y-auto">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                {templates.map((mp) => (
                  <div key={mp.templateId} className="p-3.5 rounded-xl bg-slate-50 border border-slate-100 flex flex-col justify-between space-y-2">
                    <div>
                      <div className="flex items-center justify-between">
                        <span className="text-xs font-bold text-slate-700">{mp.name}</span>
                        <span className="text-[10px] px-2 py-0.5 rounded font-medium bg-slate-100 text-slate-400">{mp.category}</span>
                      </div>
                      <p className="text-[11px] text-slate-400 mt-1">{mp.description}</p>
                    </div>
                    <button
                      disabled={isAllView}
                      onClick={async () => {
                        if (isAllView) {
                          showToast('Select a business from the header first, then add the integration');
                          return;
                        }
                        if (mp.available) {
                          // Adapter implemented — open the per-tenant config flow.
                          setShowMarketplaceModal(false);
                          handleAddConnection(mp, selectedPortco);
                        } else {
                          // No adapter yet — record an honest provision request.
                          try {
                            const res = await apiFetch(`${API_BASE}/integrations/provision-request`, {
                              method: 'POST',
                              headers: { 'Content-Type': 'application/json' },
                              body: JSON.stringify({
                                templateId: mp.templateId,
                                portcoId: selectedPortco,
                                note: `Requested from Integration Marketplace for ${selectedPortcoName}`,
                              }),
                            });
                            const body = await res.json().catch(() => ({} as any));
                            if (!res.ok) {
                              showToast(`✗ ${body.error || 'Request failed'}`);
                            } else {
                              showToast(`Requested ${mp.name} provisioning for ${selectedPortcoName}`);
                              refresh();
                            }
                          } catch (err: any) {
                            showToast(`✗ Network error: ${err.message || 'request failed'}`);
                          }
                        }
                      }}
                      className={`w-full text-center py-1.5 text-xs font-semibold rounded-lg transition-all ${
                        isAllView
                          ? 'bg-slate-100 text-slate-400 border border-slate-200 cursor-not-allowed'
                          : 'bg-slate-100 hover:bg-mantis-50 text-slate-500 hover:text-mantis-500 border border-slate-200 hover:border-cyan-800'
                      }`}
                    >
                      {isAllView
                        ? 'Select a Business'
                        : mp.available
                        ? 'Add Connection'
                        : 'Request Provisioning'}
                    </button>
                  </div>
                ))}
              </div>
            </div>

            <div className="px-6 py-4 border-t border-slate-100 flex justify-end">
              <button
                onClick={() => setShowMarketplaceModal(false)}
                className="px-4 py-2 rounded-xl text-xs font-semibold bg-slate-100 hover:bg-slate-100 text-slate-500 border border-slate-200"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};
